import { createPasswordPolicy } from '@acme/b2b/security';
import {
  AdvancedFilterField,
  AdvancedFiltersComponentModule,
  AdvancedFiltersModel,
  AdvancedFiltersService,
  AdvancedFiltersServiceModule,
} from '@b2b/common/components/advanced-filters';
import { NoticeFactory } from '@b2b/common/factories/notice';
import { capFirstLetter } from '@b2b/common/filters/capitalizeFirstLetter/capitalizeFirstLetter.filter';
import { ThemesData } from '@b2b/common/resolvers';
import { ApiService } from '@b2b/common/services/api/types';
import { AuthenticationService, AuthenticationServiceModule } from '@b2b/common/services/auth/authentication.service';
import { PermissionsService } from '@b2b/common/services/permissions';
import { SidebarService, SidebarServiceModule } from '@b2b/common/services/sidebar';
import { ThemesHelperService } from '@b2b/common/services/themesHelper';
import { ACMEResponse, ColumnDef, TODO, UIBModalService } from '@b2b/types/core';
import { UserListItem } from '@b2b/types/user';
import uiRouter, { StateService } from '@uirouter/angularjs';
import angular, { IDocumentService, IFilterService, IScope, ITimeoutService } from 'angular';

type UserListItemModel = UserListItem & {
  rolesDisplay: string;
  hideActionButton: boolean;
  passwordExpiresOnDisplay?: string;
  passwordDaysRemainingDisplay?: number | string;
  passwordStatusDisplay?: string;
};

UsersListController.$inject = [
  '$scope',
  '$state',
  '$filter',
  '$timeout',
  '$document',
  '$uibModal',
  'ApiService',
  'HelpersService',
  'NoticeFactory',
  'GridStateFactory',
  'ThemesData',
  'ThemesHelperService',
  'PermissionsService',
  'AuthenticationService',
  'GridDefinitionFactory',
  'AdvancedFiltersService',
  'SidebarService',
  'UserPreferencesService',
];

function UsersListController(
  $scope: IScope & { usersList: TODO },
  $state: StateService,
  $filter: IFilterService,
  $timeout: ITimeoutService,
  $document: IDocumentService,
  $uibModal: UIBModalService,
  ApiService: ApiService,
  HelpersService: TODO,
  NoticeFactory: NoticeFactory,
  gridStateFactory: TODO,
  ThemesData: { data: ACMEResponse<ThemesData> },
  ThemesHelperService: ThemesHelperService,
  PermissionsService: PermissionsService,
  authenticationService: AuthenticationService,
  gridDefinitionFactory: TODO,
  advancedFiltersService: AdvancedFiltersService,
  sidebarService: SidebarService,
  userPreferencesService: TODO
) {
  const ctrl = {
    loading: false,
    usersListGridState: new gridStateFactory(),
    usersListGridDefinition: new gridDefinitionFactory(),
    usersListGridName: $state.current.name + '.grid-0',
    viewId: $state.current.name,

    dateFormat: '',

    usersListGridOptions: {
      rowHeight: 46,
      useAccessibleGrid: true,
      data: 'usersList.filtered',
      appScopeProvider: {
        onDataGridPaginate: (newPageNumber, newPageSize, sortField, sortDirection) => {
          ctrl.onDataGridPaginate(newPageNumber, newPageSize, sortField, sortDirection);
        },
        onDataGridSortChange: function (grid, columns, page) {
          ctrl.onDataGridSortChange(grid, columns, page);
        },

        showInviteLink: (row: { entity: { status: string } }) => {
          if (authenticationService.isSSO()) {
            return false;
          }
          return row.entity.status === 'Unconfirmed';
        },
        resendInvite: function (e, row, id) {
          ctrl.resendInvite(id);
        },
        view: function (id) {
          ctrl.view(id);
        },
        edit: function (id) {
          ctrl.edit(id);
        },
        activate: function (row) {
          ctrl.activate(row.entity);
        },
        deactivate: function (row) {
          ctrl.deactivate(row.entity);
        },
      },
      columnDefs: [
        {
          field: 'profileImage',
          displayName: 'Photo',
          onCellNavClick: (row, col) => {
            ctrl.usersListGridOptions.appScopeProvider.view(row.entity.id);
          },
          cellTemplate: createGridImageLinkCell({ testid: 'users-list-profile-image' }),
          visible: true,
          width: 48,
        },
        {
          field: 'lastName',
          displayName: 'Last Name',
          onCellNavClick: (row, col) => {
            ctrl.usersListGridOptions.appScopeProvider.view(row.entity.id);
          },
          cellTemplate: createGridLinkCell({ testid: 'users-list-last-name' }),
          visible: true,
        },
        {
          field: 'firstName',
          displayName: 'First Name',
          onCellNavClick: (row, col) => {
            ctrl.usersListGridOptions.appScopeProvider.view(row.entity.id);
          },
          cellTemplate: createGridLinkCell({ testid: 'users-list-first-name' }),
          visible: true,
        },
        {
          field: 'department',
          displayName: 'Department',
          cellTemplate: createGridCell({ testid: 'users-list-department' }),
          visible: true,
        },
        {
          field: 'userName',
          displayName: 'Username',
          onCellNavClick: (row, col) => {
            ctrl.usersListGridOptions.appScopeProvider.view(row.entity.id);
          },
          cellTemplate: createGridLinkCell({ testid: 'users-list-user-name' }),
          visible: true,
        },
        {
          field: 'email',
          displayName: 'Email',
          onCellNavClick: (row, col) => {
            ctrl.usersListGridOptions.appScopeProvider.view(row.entity.id);
          },
          cellTemplate: createGridLinkCell({ testid: 'users-list-email' }),
          visible: true,
        },
        {
          field: 'phoneNumber',
          displayName: 'Phone Number',
          cellTemplate: createGridCell({ testid: 'users-list-phone-number' }),
          visible: true,
        },
        {
          field: 'title',
          displayName: 'Job Title',
          cellTemplate: createGridCell({ testid: 'users-list-title' }),
          visible: true,
        },
        {
          field: 'status',
          displayName: 'Status',
          cellTemplate: createGridCell({ testid: 'users-list-status' }),
          visible: true,
        },

        {
          field: 'rolesDisplay',
          displayName: 'Role',
          onCellNavClick: (row, col) => {
            ctrl.usersListGridOptions.appScopeProvider.view(row.entity.id);
          },
          cellTemplate: createGridRolesLinkCell({ testid: 'users-list-roles' }),
          visible: true,
        },
        {
          field: 'resendInvite',
          displayName: 'Invite',
          onCellNavClick: (row, col) => {
            const e = null;
            ctrl.usersListGridOptions.appScopeProvider.resendInvite(e, row, row.entity.id);
          },
          cellTemplate: createGridInviteLinkCell({ testid: 'users-list-resend-invite' }),
          enableHiding: false,
          enableSorting: false,
          visible: true,
        },
        {
          field: 'action',
          displayName: 'Action',
          onCellNavClick: (row, col) => {
            if (row.entity.active) {
              ctrl.usersListGridOptions.appScopeProvider.deactivate(row);
            } else {
              ctrl.usersListGridOptions.appScopeProvider.activate(row);
            }
          },
          enableHiding: false,
          enableSorting: false,
          cellTemplate: createGridActionCell({ testid: 'users-list-action' }),
        },
      ] as ColumnDef[],
    },

    advancedFiltersFields: [
      {
        ndx: 0,
        type: 'section',
        label: 'Search by any combination of these fields:',
      },
    ] as AdvancedFilterField[],

    paramsCustomFields: {
      operator: 'and',
      fields: [],
    } as Pick<AdvancedFiltersModel, 'operator' | 'fields'>,

    initted: false,
    model: [] as UserListItemModel[],
    filtered: [] as TODO[],
    departments: [] as TODO[],
    permissions: [] as TODO[],
    titles: [] as TODO[],
    status: ['Unconfirmed', 'Active'],

    filter: {
      searchQuery: '',
      department: null,
      title: null,
      role: null,
      passwordStatus: null,
    } as TODO,

    searchQuery: '',
    notice: new NoticeFactory(),

    init: function () {
      $scope.$emit('Application::contentTitleChange', {
        title: 'Manage Users',
        buttonRight: {
          permissions: ['USERS_INVITE', 'USERS_CREATE'],
          label: 'New User',
          action: () => {
            ctrl.invite();
          },
        },
      });

      $scope.$emit('Application::viewChange', { view: 'full' });

      $scope.$on('$destroy', ctrl.onDestroy);

      if (ThemesData && ThemesData.data.success) {
        ThemesHelperService.setModel(ThemesData.data.payload);
      }

      ctrl.initUsersListGrid();
      ctrl.setListActions();

      $scope.$on('$destroy', $scope.$on('DataGrid::UserPreferencesLoaded', ctrl.initData));
    },

    initUsersListGrid: function () {
      ctrl.setPasswordStatusGridColumnsDefinition();
      ctrl.usersListGridDefinition.init(
        ctrl.usersListGridName,
        ctrl.usersListGridOptions,
        ctrl.viewId,
        $state.current.data.enablePreferences
      );
    },

    setListActions: function () {
      const editPermissionEnabled = PermissionsService.check('USERS_UPDATE');
      const editCellTemplate = editPermissionEnabled
        ? `
            <div class="clickable ui-grid-cell-contents">
                <a data-ng-if="!row.entity.hideActionButton"
                  data-ng-click="grid.appScope.edit(row.entity.id)"
                  data-testid="users-list-edit-link"
                  role="link">
                    Edit<span class="sr-only"> Details for {{ row.entity.firstName }} {{ row.entity.lastName }}</span>
                </a>
                <a data-ng-if="row.entity.hideActionButton"
                  data-ng-click="grid.appScope.view(row.entity.id)"
                  data-testid="users-list-edit-link"
                  role="link">
                    View<span class="sr-only"> Details for {{ row.entity.firstName }} {{ row.entity.lastName}}</span>
                </a>
            </div>
          `
        : `
            <div class="clickable ui-grid-cell-contents">
              <a data-ng-click="grid.appScope.view(row.entity.id)"
                data-testid="users-list-edit-link"
                role="link">View<span class="sr-only"> Details for {{ row.entity.firstName }}{{ row.entity.lastName }}</span>
              </a>
            </div>
          `;

      const editColumn: ColumnDef = {
        field: 'edit',
        displayName: editPermissionEnabled ? 'Edit' : 'View',
        width: 50,
        onCellNavClick: (row, col) => {
          if (PermissionsService.check('USERS_UPDATE')) {
            ctrl.usersListGridOptions.appScopeProvider.edit(row.entity.id);
          } else {
            ctrl.usersListGridOptions.appScopeProvider.view(row.entity.id);
          }
        },
        cellTemplate: editCellTemplate,
        visible: true,
        enableHiding: false,
        enableSorting: false,
      };

      ctrl.usersListGridOptions.columnDefs.unshift(editColumn);
    },

    initData: function () {
      ctrl.loading = true;
      ctrl.dateFormat = userPreferencesService.getDateFormat('standardDate', 'dateFormat');
      ctrl
        .initDepartments()
        .then(ctrl.initPermissions)
        .then(ctrl.initTitles)
        .then(ctrl.initUsers)
        .then(ctrl.success.initData)
        .catch((response) => ctrl.error.initData(response));
    },

    initDepartments: function () {
      return ApiService.departments.list(HelpersService.getPaginator());
    },

    // Requires chained response from initDepartments
    initPermissions: function (response) {
      ctrl.departments = response.data.payload.list;
      return ApiService.userProxy.permissions.list(HelpersService.getPaginator());
    },

    // Requires chained response from initPermissions
    initTitles: function (response) {
      ctrl.permissions = response.data.payload.list;
      return ApiService.userProxy.titles.list(HelpersService.getPaginator());
    },

    // Requires chained response from initTitles
    initUsers: function (response) {
      ctrl.titles = response.data.payload.list;

      if ($state.params && $state.params.deptid) {
        ctrl.filter.department = ctrl.findDepartment(parseInt($state.params.deptid, 10));
      }

      if ($state.params && $state.params.roleid) {
        ctrl.filter.role = ctrl.findRole(parseInt($state.params.roleid, 10));
      }

      if ($state.params && $state.params.title) {
        ctrl.filter.title = ctrl.findTitle($state.params.title);
      }

      return ctrl.loadUsers();
    },

    loadUsers: function () {
      // Returns either filtered user list if query params exist or all users
      if ($state.params && ($state.params.deptid || $state.params.role || $state.params.title)) {
        const query = ctrl.getFilterOptions();
        HelpersService.decorate(query, ctrl.usersListGridState.getGridState());
        return ApiService.userProxy.list(query);
      } else {
        return ApiService.userProxy.list(ctrl.usersListGridState.getGridState());
      }
    },

    invite: function () {
      $uibModal
        .open({
          templateUrl: 'common/includes/inviteModal.tpl.html',
          controller: 'AddUserController',
          resolve: {
            DepartmentsData: () => ctrl.departments,
            PasswordPolicyData: () => {
              if (!ThemesData.data.success) {
                return null;
              }

              return createPasswordPolicy({
                passwordSecurityPolicyEnabled: ThemesData.data.payload.company.passwordSecurityPolicyEnabled,
                passwordMinimumLength: ThemesData.data.payload.company.passwordMinimumLength,
                passwordUseMixedCase: ThemesData.data.payload.company.passwordUseMixedCase,
                passwordMinimumDigits: ThemesData.data.payload.company.passwordMinimumDigits,
                passwordMinimumSymbols: ThemesData.data.payload.company.passwordMinimumSymbols,
              });
            },
          },
        })
        .result.then(() => ctrl.onInviteSuccess())
        .catch(() => {
          // noop
        });
    },

    search: function () {
      ctrl.filtered = $filter('filter')(ctrl.model, (user: TODO) => {
        const first = user.firstName || '';
        const last = user.lastName || '';
        const username = user.userName || '';
        const value = (first + ' ' + last + ' ' + username + ' ' + user.email).toLowerCase();
        return value.indexOf(ctrl.searchQuery.toLowerCase()) !== -1 ? user : null;
      });
    },

    findDepartment: function (id) {
      for (let i = 0, ii = ctrl.departments.length; i < ii; i++) {
        if (id === ctrl.departments[i].id) {
          return ctrl.departments[i];
        }
      }

      return false;
    },

    findRole: function (id) {
      for (let i = 0, ii = ctrl.permissions.length; i < ii; i++) {
        if (id === ctrl.permissions[i].id) {
          return ctrl.permissions[i];
        }
      }

      return false;
    },

    findTitle: function (title) {
      const index = ctrl.titles.indexOf(title);

      return index === 0 ? ctrl.titles[index] : false;
    },

    findUser: function (id, list) {
      for (let i = 0, ii = list.length; i < ii; i++) {
        if (list[i].id === id) {
          return {
            index: i,
            item: list[i],
          };
        }
      }

      return { index: -1 };
    },

    view: function (id) {
      $state.go('application.users.detail', { id: id });
    },

    edit: function (id) {
      $state.go('application.users.edit', { id: id });
    },

    deactivate: function (user) {
      user.active = false;

      ApiService.user
        .update(user)
        .then((response) => ctrl.success.deactivate(response, user.id))
        .catch((response) => ctrl.error.deactivate(response));
    },

    activate: function (user) {
      user.active = true;

      ApiService.user
        .update(user)
        .then((response) => ctrl.success.activate(response, user.id))
        .catch((response) => ctrl.error.activate(response));
    },

    resendInvite: function (id) {
      const user = ctrl.findUser(id, ctrl.filtered);

      if (user.index > -1) {
        ApiService.userProxy
          .resendInvite(id)
          .then((response) => {
            ctrl.filtered[user.index].sent = true;
            ctrl.success.resendInvite(response);
          })
          .catch((response) => ctrl.error.resendInvite(response));
      }
    },

    getUsers: function (options) {
      ApiService.userProxy
        .list(options)
        .then((response) => ctrl.success.getUsers(response))
        .catch((response) => ctrl.error.getUsers(response));
    },

    processUsers: function (list: UserListItemModel[]): UserListItemModel[] {
      return list.map((user) => {
        user.rolesDisplay = user.roles.map((role) => role.name).join(', ');

        user.hideActionButton = user.category === 'internal';

        if (user.passwordStatus) {
          user.passwordStatusDisplay = capFirstLetter(user.passwordStatus.split('_').join(' ') || '');
        }

        if (user.passwordExpiresOn) {
          user.passwordExpiresOnDisplay = $filter('date')(user.passwordExpiresOn, ctrl.dateFormat);
        }

        switch (user.passwordStatus) {
          case 'ACTIVE':
            user.passwordDaysRemainingDisplay = user.passwordDaysRemaining + ' days';
            break;
          case 'EXPIRING_SOON':
            user.passwordDaysRemainingDisplay = user.passwordDaysRemaining + ' days';
            break;
          case 'EXPIRED':
            user.passwordDaysRemainingDisplay = 'Expired';
            break;
          default:
            user.passwordDaysRemainingDisplay = '';
        }

        return user;
      });
    },

    refreshUsersListGrid: function (response) {
      ctrl.usersListGridDefinition.isLoaded = true;
      $timeout(function () {
        $scope.$broadcast('DataGrid::ResizeGrid', {
          name: ctrl.usersListGridDefinition.options.gridName,
          datasetLength: ctrl.model.length,
          totalItems: response.data.payload.pagination.count,
        });
      }, 10);
    },

    setPasswordStatusGridColumnsDefinition: function () {
      const passwordStatusColumns: ColumnDef[] = [
        {
          field: 'passwordStatusDisplay',
          displayName: 'Password Status',
          visible: false,
        },
        {
          field: 'passwordDaysRemainingDisplay',
          displayName: 'Password Expiring In',
          visible: true,
        },
        {
          field: 'passwordExpiresOnDisplay',
          displayName: 'Password Expires On',
          visible: false,
        },
      ];

      if (authenticationService.isPasswordRotationEnabled()) {
        ctrl.usersListGridOptions.columnDefs.splice(10, 0, ...passwordStatusColumns);
      }
    },

    setAdvancedFiltersFields: function () {
      const filters: AdvancedFilterField[] = [
        {
          ndx: 1,
          type: 'field',
          label: 'Search',
          value: 'searchQuery',
          placeholderValue: 'Search users...',
          format: 'Text',
        },
        {
          ndx: 2,
          type: 'field',
          label: 'Department',
          value: 'department',
          placeholderValue: 'All departments',
          format: 'Select',
          selectable: ctrl.departments.map((department) => ({
            label: department.name,
            value: department.id,
          })),
        },
        {
          ndx: 3,
          type: 'field',
          label: 'Job Title',
          value: 'title',
          placeholderValue: 'All job titles',
          format: 'Select',
          selectable: ctrl.titles.map((title, index) => ({
            label: title,
            value: index,
          })),
        },
        {
          ndx: 4,
          type: 'field',
          label: 'Authorization',
          value: 'role',
          placeholderValue: 'All authorization roles',
          format: 'Select',
          selectable: ctrl.permissions.map((role) => ({
            label: role.name,
            value: role.id,
          })),
        },
      ];

      ctrl.advancedFiltersFields.push(...filters);

      if (authenticationService.isPasswordRotationEnabled()) {
        ctrl.advancedFiltersFields.push({
          ndx: 5,
          type: 'field',
          label: 'Password Status',
          value: 'passwordStatus',
          placeholderValue: 'All password statuses',
          format: 'Select',
          selectable: [
            { label: 'Active', value: 'ACTIVE' },
            { label: 'Expiring soon', value: 'EXPIRING_SOON' },
            { label: 'Expired', value: 'EXPIRED' },
          ],
        });
      }
    },

    setFilterOptionsFromAdvancedFilterModel: function (props: AdvancedFiltersModel) {
      props.fields.forEach((field) => {
        switch (field.name) {
          case 'searchQuery':
          case 'passwordStatus':
            ctrl.filter[field.name] = field.values.join(',') || null;
            break;
          case 'department':
          case 'role':
            ctrl.filter[field.name] = {
              id: field.values.join(','),
              name: field.label,
            };
            break;
          case 'title':
            ctrl.filter[field.name] = field.label || null;
            break;
          default:
            break;
        }
      });
    },

    getFilterOptions: function () {
      return {
        search: ctrl.filter.searchQuery || null,
        departmentId: ctrl.filter.department ? ctrl.filter.department.id || null : null,
        jobTitle: ctrl.filter.title || null,
        role: ctrl.filter.role ? ctrl.filter.role.name || null : null,
        passwordStatus: ctrl.filter.passwordStatus || null,
      };
    },

    clearFilters: function () {
      ctrl.searchQuery = '';
      ctrl.clearAdvancedFilters();
    },

    clearAdvancedFilters: function () {
      ctrl.filter.searchQuery = '';
      ctrl.filter.department = null;
      ctrl.filter.title = null;
      ctrl.filter.role = null;
      ctrl.filter.passwordStatus = null;
    },

    success: {
      initData: function (response) {
        ctrl.initted = true;
        ctrl.model = ctrl.processUsers(response.data.payload.list);
        ctrl.usersListGridState.setPropertyValue('totalItems', response.data.payload.pagination.count);

        // Perform a search if there is a query parmeter
        if ($state.params && $state.params.search) {
          ctrl.searchQuery = $state.params.search;
        }

        ctrl.search();
        ctrl.refreshUsersListGrid(response);
        ctrl.setAdvancedFiltersFields();
        ctrl.loading = false;
      },

      getUsers: function (response) {
        ctrl.model = ctrl.processUsers(response.data.payload.list);
        ctrl.usersListGridState.setPropertyValue('totalItems', response.data.payload.pagination.count);
        ctrl.search();

        ctrl.refreshUsersListGrid(response);
        ctrl.loading = false;
      },

      deactivate: function (response, userId) {
        for (let i = 0, ii = ctrl.filtered.length; i < ii; i++) {
          if (ctrl.filtered[i].id === userId) {
            ctrl.filtered[i].active = false;
            ctrl.filtered[i].status = 'Deactivated';
            break;
          }
        }
      },

      activate: function (response, userId) {
        for (let i = 0, ii = ctrl.filtered.length; i < ii; i++) {
          if (ctrl.filtered[i].id === userId) {
            ctrl.filtered[i].active = true;
            ctrl.filtered[i].status = 'Active';
            break;
          }
        }
      },

      resendInvite: function (response) {
        console.log(response);
      },

      onFilterUpdate: function (response) {
        ctrl.success.getUsers(response);
        ctrl.search();
        ctrl.loading = false;
      },
    },

    error: {
      initData: function (response) {
        console.log(response);
      },
      getUsers: function (response) {
        console.log(response);
      },
      activate: function (response) {
        console.log(response);
      },
      deactivate: function (response) {
        console.log(response);
      },
      resendInvite: function (response) {
        console.log(response);
      },
      onFilterUpdate: function (response) {
        console.log(response);
      },
    },

    onDestroy: function () {
      $scope.$emit('Application::viewChange', { view: 'sidebar' });
      $document.off('click');
    },

    onInviteSuccess: function () {
      ctrl
        .loadUsers()
        .then((response) => ctrl.success.initData(response))
        .catch((response) => ctrl.error.initData(response));
    },

    onDataGridPaginate: function (newPage, pageSize, sortField, sortDirection) {
      let query = ctrl.getFilterOptions();
      ctrl.usersListGridState.updateGridState(newPage, pageSize);
      if (sortField) {
        ctrl.usersListGridState.updateGridState(null, null, sortField, null);
      }
      if (sortDirection) {
        ctrl.usersListGridState.updateGridState(null, null, null, sortDirection);
      }

      query = { ...ctrl.usersListGridState.getGridState(), ...query };

      if (!query.search) {
        query = { ...query, ...{ search: ctrl.searchQuery } };
      }

      ctrl.getUsers(query);
    },

    onDataGridSortChange: function (grid, columns, page) {
      const data = columns[0];
      let query = ctrl.getFilterOptions();

      if (data) {
        ctrl.usersListGridState.updateGridState(page || null, null, data.field, data.sort.direction);
      }

      query = { ...ctrl.usersListGridState.getGridState(), ...query };

      if (!query.search) {
        query = { ...query, ...{ search: ctrl.searchQuery } };
      }

      ctrl.getUsers(query);
    },

    onSearchQueryUpdate: function () {
      ctrl.loading = true;
      ctrl.clearAdvancedFilters();
      advancedFiltersService.clearAllComponentFilters({ name: 'usersList', emitEvent: false });
      const query = { ...ctrl.usersListGridState.getGridState(), ...{ search: ctrl.searchQuery } };
      ctrl.getUsers(query);
    },

    onAdvancedFiltersSubmit(props: AdvancedFiltersModel) {
      ctrl.searchQuery = '';
      ctrl.clearFilters();
      ctrl.setFilterOptionsFromAdvancedFilterModel(props);
      const query = { ...ctrl.usersListGridState.getGridState(), ...ctrl.getFilterOptions() };
      ctrl.getUsers(query);
    },

    onAdvancedFiltersClear() {
      ctrl.paramsCustomFields.fields = [];
      ctrl.paramsCustomFields.operator = 'and';
      ctrl.clearFilters();
      ctrl.onSearchQueryUpdate();
    },

    onAdvancedFiltersClose() {
      sidebarService.hide();
    },
  };

  $scope.usersList = ctrl;
  ctrl.init();
}

export const UsersListControllerModule = angular
  .module('acme.view.application.users.list.controllers', [
    uiRouter,
    'acme.common.factory.notice',
    'acme.common.service.api',
    'acme.common.service.helpers',
    'acme.common.service.themesHelper',
    'acme.common.service.permissions',
    'acme.common.factory.gridState',
    AuthenticationServiceModule,
    AdvancedFiltersServiceModule,
    AdvancedFiltersComponentModule,
    SidebarServiceModule,
    'acme.common.component.filterbar',
    'acme.common.service.userPreferences',
  ])
  .controller('UsersListController', UsersListController).name;

function createGridCell(props: { testid: string }): string {
  return `
    <div class="ui-grid-cell-contents"
         data-testid="${props.testid}">{{ grid.getCellValue(row, col) }}</div>
  `;
}

function createGridLinkCell(props: { testid: string }): string {
  return `
    <div class="ui-grid-cell-contents">
      <a data-ng-if="fieldVal = grid.getCellValue(row, col)"
         data-ng-click="grid.appScope.view(row.entity.id)"
         data-testid="${props.testid}"
         role="button">{{ fieldVal }}<span class="sr-only">View Details for {{ fieldVal }}</span>
      </a>
    </div>
  `;
}

function createGridImageLinkCell(props: { testid: string }): string {
  return `
    <div class="ui-grid-cell-contents">
      <a class="profile-image"
         data-testid="${props.testid}"
         data-ng-click="grid.appScope.view(row.entity.id)"
         data-ng-class="{ nopic: !grid.getCellValue(row, col) }">
        <span class="pic"
              data-ng-if="grid.getCellValue(row, col)"
              data-ng-style="{ 'background-image': 'url(/v1/api/photos/{{ grid.getCellValue(row, col) }}/PTN)' }"></span>
        <span class="sr-only">User id {{ row.entity.id }} profile image</span>
      </a>
    </div>
  `;
}

function createGridInviteLinkCell(props: { testid: string }): string {
  return `
    <div class="ui-grid-cell-contents">
      <a data-permit="USERS_INVITE"
         role="button"
         class="btn btn-info btn-xs"
         data-testid="${props.testid}"
         data-ng-click="grid.appScope.resendInvite($event, row, row.entity.id)"
         data-ng-show="grid.appScope.showInviteLink(row)">
        <span data-ng-hide="row.entity.sent">Resend Invite</span>
        <span data-ng-show="row.entity.sent">Send Again</span>
      </a>
    </div>
  `;
}

function createGridRolesLinkCell(props: { testid: string }): string {
  return `
    <div class="ui-grid-cell-contents">
      <div class="role"
           data-testid="${props.testid}">{{ COL_FIELD }}</div>
    </div>
  `;
}

function createGridActionCell(props: { testid: string }): string {
  return `
    <div class="ui-grid-cell-contents">
      <span data-ng-if="!row.entity.hideActionButton">
        <a class="btn btn-xs btn-info"
            data-ng-if="!row.entity.active"
            data-testid="${props.testid}"
            data-ng-click="grid.appScope.activate(row)">Activate</a>
        <a class="btn btn-xs btn-info"
            data-ng-if="row.entity.active"
            data-testid="${props.testid}"
            data-ng-click="grid.appScope.deactivate(row)">Deactivate</a>
      </span>
    </div>
  `;
}
